// App detail ("Manage App"), shown when an app is managed from the Catalog.
// to the app). Fetches GET /apps/{app} for the version history + live status, and offers an explicit
// link to actually open the running app. `app` is the catalog row (name, version, routingMode,
// ownerGroup); `onBack` returns to the catalog.
import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import WidgetsIcon from "@mui/icons-material/Widgets";
import DeployedCodeUpdateIcon from "@mui/icons-material/BrowserUpdated";
import HubIcon from "@mui/icons-material/Hub";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PublicIcon from "@mui/icons-material/Public";
import RestoreIcon from "@mui/icons-material/Restore";
import { alpha } from "@mui/material/styles";

import type { App, AppDetail as AppDetailData, Client } from "../types";
import { MONO } from "../theme";
import { IconTile, StatusBadge } from "../components/ui";
import { appHref } from "./Catalog";
import { ConfigEditor } from "./Config";
import { DeployDialog } from "./DeployDialog";

// Format an epoch-ms timestamp as "Apr 2, 2026 · 09:12" (the board's Deployed column). Blank if absent.
function formatDeployed(ms?: number): string {
  if (!ms) return "-";
  const d = new Date(ms);
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${date}, ${time}`;
}

// A small labeled meta item (icon + text) for the hero's meta row.
function Meta({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: "text.secondary" }}>
      <Box sx={{ display: "grid", "& .MuiSvgIcon-root": { fontSize: 18 } }}>{icon}</Box>
      <Typography variant="body2">{label}</Typography>
    </Stack>
  );
}

interface AppDetailProps {
  client: Client;
  app: App;
  onBack: () => void;
}

export function AppDetail({ client, app, onBack }: AppDetailProps) {
  const [detail, setDetail] = useState<AppDetailData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deploying, setDeploying] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // Version string currently being rolled to (disables that row's button + shows a spinner); null idle.
  const [rollingTo, setRollingTo] = useState<string | null>(null);
  const [rollbackError, setRollbackError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setDetail(null);
    setError(null);
    client
      .getAppDetail(app.name)
      .then((d) => active && setDetail(d))
      .catch((e) => active && setError((e as Error).message));
    return () => {
      active = false;
    };
  }, [client, app.name, reloadKey]);

  const openHref = appHref(app, client.appDomain);
  const domainLabel = openHref.replace(/^https?:\/\//, "").replace(/\/$/, "");

  // Roll the live pointer to a prior approved version, then refresh the table to reflect the switch.
  async function rollback(version: string) {
    setRollbackError(null);
    setRollingTo(version);
    try {
      await client.rollbackApp(app.name, version);
      setReloadKey((k) => k + 1);
    } catch (e) {
      setRollbackError((e as Error).message);
    } finally {
      setRollingTo(null);
    }
  }

  if (deploying) {
    return (
      <DeployDialog
        client={client}
        expectedApp={app.name}
        onClose={() => setDeploying(false)}
        onDeployed={() => {
          setDeploying(false);
          setReloadKey((k) => k + 1); // refresh the version table after a successful deploy
        }}
      />
    );
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ ml: -1 }}>
          Back to apps
        </Button>
      </Box>

      {/* Hero header: avatar, title, badges, meta, and the primary actions. */}
      <Paper elevation={1} sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5} alignItems="flex-start">
          <IconTile icon={<WidgetsIcon />} accent="primary" size={56} radius={1} iconFontSize={30} />

          <Stack spacing={1.25} sx={{ flexGrow: 1, minWidth: 240 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h4" component="h1">
                {app.name}
              </Typography>
              {app.ownerGroup && <Chip size="small" label={app.ownerGroup} />}
              {detail?.deprecated ? (
                <Chip size="small" color="error" label="deprecated" />
              ) : (
                <StatusBadge label="Live" accent="success" />
              )}
            </Stack>
            <Stack direction="row" spacing={2.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <Meta icon={<HubIcon />} label={`Routing: ${app.routingMode || "path"}`} />
              <Meta icon={<PublicIcon />} label={domainLabel} />
              <Meta
                icon={<DeployedCodeUpdateIcon />}
                label={`Live version ${detail?.currentVersion || "none"}`}
              />
            </Stack>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <Button
              variant="outlined"
              endIcon={<OpenInNewIcon />}
              component={Link}
              href={openHref}
              target="_blank"
              rel="noreferrer"
            >
              Open app
            </Button>
            {/* Deploy a new version, gated by canManage (owner group or admin), same as the API. */}
            {app.canManage && (
              <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={() => setDeploying(true)}>
                Deploy new version
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {error && <Alert severity="error">Failed to load details: {error}</Alert>}
      {!error && detail === null && (
        <Stack spacing={2} aria-label="Loading details">
          <Skeleton variant="rounded" height={176} />
          <Skeleton variant="rounded" height={260} />
          <Typography sx={{ position: "absolute", width: 1, height: 1, overflow: "hidden" }}>
            Loading details
          </Typography>
        </Stack>
      )}

      {detail && (
        <>
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="h6">Versions</Typography>
              <Chip
                size="small"
                label={`${detail.versions.length} version${detail.versions.length === 1 ? "" : "s"}`}
              />
            </Stack>
            {rollbackError && (
              <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setRollbackError(null)}>
                Rollback failed: {rollbackError}
              </Alert>
            )}
            {detail.versions.length === 0 ? (
              <Alert severity="info">No versions registered.</Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table sx={{ minWidth: 760 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>VERSION</TableCell>
                      <TableCell sx={{ width: 200 }}>STATUS</TableCell>
                      <TableCell sx={{ width: 160 }}>FLAGS</TableCell>
                      <TableCell sx={{ width: 240 }}>DEPLOYED</TableCell>
                      {app.canManage && <TableCell sx={{ width: 150 }}>ACTIONS</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.versions.map((v) => {
                      const isCurrent = v.version === detail.currentVersion;
                      const flags =
                        [v.isDeprecated && "deprecated", v.isHidden && "hidden"]
                          .filter(Boolean)
                          .join(", ") || "-";
                      // A version is rollback-eligible when it's approved, not the live one, and not
                      // Deprecated versions cannot be rollback targets, matching the backend guard.
                      const canRollTo =
                        v.status === "APPROVED" && !isCurrent && !v.isDeprecated;
                      return (
                        <TableRow
                          key={v.version}
                          sx={
                            isCurrent
                              ? {
                                  bgcolor: (theme) =>
                                    alpha(
                                      theme.palette.primary.main,
                                      theme.palette.mode === "dark" ? 0.1 : 0.04,
                                    ),
                                }
                              : undefined
                          }
                        >
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Box component="span" sx={{ fontFamily: MONO, fontWeight: isCurrent ? 600 : 400 }}>
                                {v.version}
                              </Box>
                              {isCurrent && (
                                <Chip
                                  size="small"
                                  label="current"
                                  sx={(theme) => ({
                                    height: 20,
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      theme.palette.mode === "dark" ? 0.16 : 0.08,
                                    ),
                                    color: "primary.dark",
                                    border: "1px solid",
                                    borderColor: "primary.main",
                                    "& .MuiChip-label": { fontSize: 12, px: 1 },
                                  })}
                                />
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            {v.status === "APPROVED" ? (
                              <StatusBadge label="APPROVED" accent="success" />
                            ) : (
                              <StatusBadge label={v.status} accent="warning" />
                            )}
                          </TableCell>
                          <TableCell sx={{ color: "text.secondary" }}>{flags}</TableCell>
                          <TableCell sx={{ color: "text.secondary" }}>{formatDeployed(v.createdAt)}</TableCell>
                          {app.canManage && (
                            <TableCell>
                              {canRollTo && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={
                                    rollingTo === v.version ? (
                                      <CircularProgress size={14} />
                                    ) : (
                                      <RestoreIcon />
                                    )
                                  }
                                  disabled={rollingTo !== null}
                                  onClick={() => rollback(v.version)}
                                >
                                  Make current
                                </Button>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>

          <Box>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="h6">Configuration</Typography>
              {app.canManage && <Chip size="small" label="you can edit" />}
            </Stack>
            <ConfigEditor client={client} appName={app.name} variant="detail" />
          </Box>
        </>
      )}
    </Stack>
  );
}
